import React, { useEffect, useMemo, useRef } from "react";
import Chart from "chart.js/auto";
import { useTranslation } from "react-i18next";

const stripedBackground = {
  id: "stripedBackground",
  beforeDraw: (chart: any) => {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;
    const { left, right, top, bottom, width } = chartArea;
    const xScale = scales.x;

    ctx.save();
    const ticks = xScale.ticks?.length || xScale.getLabelItems().length || 4;
    const step = width / ticks;

    for (let i = 0; i < ticks; i++) {
      if (i % 2 === 1) {
        ctx.fillStyle = "rgba(203, 213, 225, 0.12)"; // slate-300 ~ 12%
        ctx.fillRect(left + i * step, top, step, bottom - top);
      }
    }
    ctx.restore();
  },
};

type Props = {
  labels?: string[];      
  sales?: number[];       
  shrink?: number[];      
  positiveTotal?: number;
};

const TwoLinesWithPercentTable: React.FC<Props> = ({
  labels = ["Week 1", "Week 2", "Week 3", "Week 4"],
  sales  = [6, 10, 4, 6],
  shrink = [5, 2, 20, 3],
  positiveTotal = 0,
}) => {
  const [t] = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    chartRef.current?.destroy();

    const gGreen = ctx.createLinearGradient(0, 0, 0, 300);
    gGreen.addColorStop(0, "rgba(0, 224, 150, 0.3)"); // emerald-500
    gGreen.addColorStop(1, "rgba(0, 224, 150, 0.05)");

    const gRed = ctx.createLinearGradient(0, 0, 0, 300);
    gRed.addColorStop(0, "rgba(240, 82, 82, 0.3)");    // red-500
    gRed.addColorStop(1, "rgba(240, 82, 82, 0.05)");

    chartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          // Sales
          {
            label: "Sales",
            data: sales,
            yAxisID: "yRight",
            borderColor: "#0E9F6E",
            pointBackgroundColor: "#0E9F6E",
            backgroundColor: gGreen,
            borderWidth: 3,
            pointRadius: 2.5,
            tension: 0.45,
            fill: "origin",
          },
          // Shrinkage
          {
            label: "Shrinkage",
            data: shrink,
            yAxisID: "yLeft",
            borderColor: "#F05252",
            pointBackgroundColor: "#F05252",
            backgroundColor: gRed,
            borderWidth: 3,
            pointRadius: 2.5,
            tension: 0.45,
            fill: "origin",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "nearest" },
        plugins: {
          legend: { 
            display: true, 
            labels: { 
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 12,
              boxHeight: 10,
              padding: 24,
              color: '#334155',
              font: { size: 14, weight: 500 },
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label(ctx) {
                const v = ctx.parsed.y ?? 0;
                return `${ctx.dataset.label}: ${v}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: "rgba(148,163,184,.25)" },
            ticks: { color: "#334155" },
          },
          yLeft: {
            beginAtZero: true,
            position: "left",
            grid: { color: "rgba(148,163,184,.25)" },
            ticks: { color: "#334155", precision: 0 },
            border: { color: "#F05252", width: 2 },
          },
          yRight: {
            beginAtZero: true,
            position: "right",
            grid: { drawOnChartArea: false },
            ticks: { color: "#334155", precision: 0 },
            border: { color: "#0E9F6E", width: 2 },
          },
        },
      },
      plugins: [stripedBackground],
    });

    return () => chartRef.current?.destroy();
  }, [labels, sales, shrink]);

  return (
    <div className="space-y-6">
      {/* Chart */}
      <div className="bg-white rounded-xl p-4">
        <div className="w-full h-72">
          <canvas ref={canvasRef} />
        </div>
      </div>
    </div>
  );
};

export default TwoLinesWithPercentTable;